import { AdminStatusDto, CandidateStatusDto, GenderDto, QuizScoreDto } from '@/api/generated';

import type { AdminViewTranslations } from './adminView.translations.pl.ts';
import { candidatesTranslations } from './candidates.translations.fr.ts';
import { dashboardTranslations } from './dashboard.translations.fr.ts';
import { fairsTranslations } from './fairs.translations.fr.ts';
import { reportsTranslations } from './reports.translations.fr.ts';

export const adminViewTranslations: AdminViewTranslations = {
  candidates: candidatesTranslations,
  dashboard: dashboardTranslations,
  details: {
    adminStatus: 'Statut administrateur',
    adminStatuses: {
      [AdminStatusDto.None]: 'Aucun',
      [AdminStatusDto.OnHoldAdmin]: 'En attente (administrateur)',
      [AdminStatusDto.OnHoldConditional]: 'En attente (conditionnel)',
    },
    phone: 'Téléphone',
  },
  preview: {
    sections: {
      additional: 'Informations complémentaires',
      basic: 'Données de base',
      dates: 'Dates',
      statusAndResults: 'Statut et résultats',
    },
  },
  filters: {
    actionPossibleFrom: 'Évaluation possible à partir du',
    actionPossibleTo: "Évaluation possible jusqu'au",
    adminStatus: 'Statut administrateur',
    adminStatuses: {
      [AdminStatusDto.OnHoldAdmin]: 'En attente - Administrateur',
      [AdminStatusDto.OnHoldConditional]: 'En attente - Conditionnel',
    },
    title: 'Filtres',
  },
  pointsLegend: {
    decision: 'Décision',
    doNotSend: 'Ne pas transmettre',
    max: 'Max',
    min: 'Min',
    score: 'Appréciation',
    send: 'Transmettre',
    title: 'Légende des points',
  },
  actions: {
    cancel: 'Annuler',
    confirm: 'Confirmer',
    confirmMessages: {
      default: 'Voulez-vous vraiment effectuer cette action ?',
      negativeFeedback: 'Voulez-vous vraiment attribuer un retour négatif à ce candidat ?',
      onHold: 'Voulez-vous vraiment mettre en attente le processus de ce candidat ?',
      positiveFeedback: 'Voulez-vous vraiment attribuer un retour positif à ce candidat ?',
    },
    confirmTitle: "Confirmer l'action",
    genderLabel: 'Genre du candidat',
    genderTitle: 'Choisir le genre',
    labels: {
      gender: 'Définir le genre',
      negativeFeedback: 'Attribuer un retour négatif',
      onHold: 'Mettre le processus en attente',
      positiveFeedback: 'Attribuer un retour positif',
    },
    noActions: 'Aucune action disponible',
    save: 'Enregistrer',
    toggle: 'Actions',
  },
  confirmPositiveFeedbackModal: {
    title: 'Confirmer le retour positif',
    body: 'Voulez-vous vraiment confirmer le retour positif ?',
    closeLabel: 'Annuler',
    confirmLabel: 'Confirmer',
  },
  search: {
    email: 'E-mail',
    phone: 'Numéro de téléphone',
    statuses: 'Statut',
    lastName: 'Nom',
    quizScore: 'Appréciation du quiz',
    locationIds: 'Lieux',
    adminActionPossibleBeforeFrom: 'Action possible à partir du',
    adminActionPossibleBeforeTo: "Action possible jusqu'au",
    isFromFair: 'Issus des salons',
    adminStatuses: 'Statuts administrateur',
  },
  buttons: {
    submit: 'Rechercher',
    clear: 'Effacer',
    logout: 'Se déconnecter',
  },
  tabs: {
    candidates: 'Candidats',
    dashboard: 'Tableau de bord',
    fairCandidates: "Salons de l'emploi",
    reports: 'Rapports',
  },
  fairs: fairsTranslations,
  reports: reportsTranslations,
  tooltips: {
    downloadCv: 'Télécharger le CV',
    downloadFullAudit: "Télécharger l'audit complet du candidat",
    downloadProcessCv: 'Télécharger le dossier du processus',
    setPositiveFeedback: "Changer l'évaluation en positive",
    viewCandidate: 'Voir le candidat',
  },
  table: {
    decisionOverridden: 'Décision modifiée',
    name: 'Candidat',
    email: 'E-mail',
    status: 'Statut',
    quizScore: 'Appréciation du quiz',
    sourceApp: 'Source',
    noOfQuizRevisits: 'Nombre de visites',
    areasOfInterests: 'Domaines métiers',
    location: 'Lieu',
    university: 'Université',
    gender: 'Genre',
    isFromFair: 'Issu des salons',
    statuses: {
      [CandidateStatusDto.SentToERecruiter]: 'Transmis à eRecruiter',
      [CandidateStatusDto.PositiveFeedback]: 'Résultat du jeu positif',
      [CandidateStatusDto.NegativeFeedback]: 'Résultat du jeu négatif',
      [CandidateStatusDto.PositiveFeedbackSent]: 'Retour positif envoyé',
      [CandidateStatusDto.NegativeFeedbackSent]: 'Retour négatif envoyé',
      [CandidateStatusDto.InQuiz]: 'En train de jouer',
      [CandidateStatusDto.QuizFinished]: 'Jeu terminé',
      [CandidateStatusDto.PersonalDataProvided]: 'Données personnelles fournies',
      [CandidateStatusDto.EmailProvided]: 'E-mail fourni',
      [CandidateStatusDto.CandidateProcessFinished]: 'Processus terminé',
      [CandidateStatusDto.EmailVerified]: 'E-mail vérifié',
      [CandidateStatusDto.QuizExpired]: 'Temps de jeu dépassé',
      [CandidateStatusDto.QuizRevisitsExceeded]: 'Limite de visites du jeu dépassée',
      [CandidateStatusDto.QuizPointsResultReady]: 'Résultat en points prêt',
    },
    quizScores: {
      [QuizScoreDto.Low]: 'Faible',
      [QuizScoreDto.Average]: 'Moyen',
      [QuizScoreDto.Good]: 'Bon',
      [QuizScoreDto.VeryGood]: 'Très bon',
      [QuizScoreDto.Outstanding]: 'Exceptionnel',
    },
    genders: {
      [GenderDto.Male]: 'Homme',
      [GenderDto.Female]: 'Femme',
      [GenderDto.Unknown]: 'Inconnu',
    },
    quizPoints: 'Points',
    statusUpdatedAt: 'Date de changement de statut',
    quizFinishedAt: 'Quiz terminé',
    adminActionPossibleBefore: "Évaluation possible jusqu'au",
    possibleAdminActionTypes: 'Actions disponibles',
    yes: 'Oui',
    no: 'Non',
  },
};
